import React, { useState, useEffect, useCallback } from 'react';
import { Button } from '../components/ui/button';
import { Bell } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { alertsAPI, signalsAPI } from '../services/api';
import { useToast } from '../hooks/use-toast';
import { useNavigate } from 'react-router-dom';
import LegalDisclaimer from '../components/LegalDisclaimer';
import TelegramCard from './alerts/TelegramCard';
import SignalsCard from './alerts/SignalsCard';
import AlertCreateCard from './alerts/AlertCreateCard';
import AlertsListCard from './alerts/AlertsListCard';
import AlertHistoryCard from './alerts/AlertHistoryCard';

const Alerts = () => {
  const { isPro, user } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();

  const [alerts, setAlerts] = useState([]);
  const [tgStatus, setTgStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ symbol: '', condition: 'price_above', threshold: '', note: '', repeat: true });
  const [events, setEvents] = useState([]);
  const [linking, setLinking] = useState(false);
  const [deepLink, setDeepLink] = useState(null);
  const [polling, setPolling] = useState(false);
  const [signalCats, setSignalCats] = useState([]);
  const [subCats, setSubCats] = useState([]);
  const [recentByCat, setRecentByCat] = useState({});
  const [runningCat, setRunningCat] = useState(null);

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [list, status, cats, sub, evts] = await Promise.all([
        alertsAPI.list(),
        alertsAPI.telegramStatus(),
        signalsAPI.categories().catch(() => []),
        signalsAPI.getSubscription().catch(() => ({ categories: [] })),
        alertsAPI.history().catch(() => []),
      ]);
      setAlerts(list || []);
      setTgStatus(status);
      setSignalCats(cats || []);
      setSubCats(sub?.categories || []);
      setEvents(evts || []);
      const byCat = {};
      await Promise.all(
        (sub?.categories || []).map(async (c) => {
          try {
            byCat[c] = await signalsAPI.recent(c, 5);
          } catch {
            byCat[c] = [];
          }
        }),
      );
      setRecentByCat(byCat);
    } catch (e) {
      console.warn('Alerts load failed:', e);
      toast({ title: 'Error', description: 'Failed to load alerts', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    if (isPro) loadAll();
  }, [isPro, loadAll]);

  // Poll Telegram link status until /start is tapped or timeout occurs.
  useEffect(() => {
    if (!deepLink || tgStatus?.linked) return undefined;
    setPolling(true);
    const handle = setInterval(async () => {
      try {
        const s = await alertsAPI.telegramStatus();
        setTgStatus(s);
        if (s?.linked) {
          setDeepLink(null);
          setPolling(false);
          toast({ title: 'Telegram linked', description: 'You will receive alerts in Telegram.' });
          clearInterval(handle);
        }
      } catch (e) {
        console.debug('Telegram status poll failed (continuing):', e);
      }
    }, 3000);
    return () => {
      clearInterval(handle);
      setPolling(false);
    };
  }, [deepLink, tgStatus?.linked, toast]);

  const toggleSignalCat = async (catId) => {
    const next = subCats.includes(catId) ? subCats.filter((c) => c !== catId) : [...subCats, catId];
    try {
      const r = await signalsAPI.setSubscription(next);
      setSubCats(r.categories || next);
      toast({ title: 'Subscriptions updated' });
      if (!subCats.includes(catId) && next.includes(catId)) {
        const items = await signalsAPI.recent(catId, 5).catch(() => []);
        setRecentByCat({ ...recentByCat, [catId]: items });
      }
    } catch (e) {
      console.warn('Subscription update failed:', e);
      toast({ title: 'Error', description: 'Failed to update', variant: 'destructive' });
    }
  };

  const runSignalNow = async (catId) => {
    setRunningCat(catId);
    try {
      const r = await signalsAPI.runNow(catId);
      toast({ title: 'Generated', description: `${r.delivered} signals delivered` });
      const items = await signalsAPI.recent(catId, 5).catch(() => []);
      setRecentByCat({ ...recentByCat, [catId]: items });
    } catch (e) {
      toast({
        title: 'Error',
        description: e?.response?.data?.detail || 'Run failed',
        variant: 'destructive',
      });
    } finally {
      setRunningCat(null);
    }
  };

  const handleCreate = async () => {
    if (!form.symbol.trim() || !form.threshold) {
      toast({ title: 'Missing fields', description: 'Symbol and threshold required', variant: 'destructive' });
      return;
    }
    try {
      const created = await alertsAPI.create({
        symbol: form.symbol.toUpperCase().trim(),
        condition: form.condition,
        threshold: Number(form.threshold),
        note: form.note.trim() || null,
        repeat: form.repeat,
      });
      setAlerts([created, ...alerts]);
      setForm({ symbol: '', condition: 'price_above', threshold: '', note: '', repeat: true });
      toast({ title: 'Alert created', description: `${created.symbol} ${created.condition} ${created.threshold}` });
    } catch (e) {
      const msg = e?.response?.data?.detail || 'Failed to create alert';
      toast({ title: 'Error', description: String(msg), variant: 'destructive' });
    }
  };

  const handleToggle = async (alert) => {
    try {
      await alertsAPI.toggle(alert.id, !alert.enabled);
      setAlerts(alerts.map((a) => (a.id === alert.id ? { ...a, enabled: !a.enabled } : a)));
    } catch (e) {
      console.warn('Toggle alert failed:', e);
      toast({ title: 'Error', description: 'Failed to toggle alert', variant: 'destructive' });
    }
  };

  const handleDelete = async (id) => {
    try {
      await alertsAPI.remove(id);
      setAlerts(alerts.filter((a) => a.id !== id));
    } catch (e) {
      console.warn('Delete alert failed:', e);
      toast({ title: 'Error', description: 'Failed to delete alert', variant: 'destructive' });
    }
  };

  const startLink = async () => {
    setLinking(true);
    try {
      const res = await alertsAPI.telegramLink();
      setDeepLink(res);
      window.open(res.deep_link, '_blank', 'noopener,noreferrer');
    } catch (e) {
      console.warn('Telegram linking failed:', e);
      toast({ title: 'Error', description: 'Telegram linking failed', variant: 'destructive' });
    } finally {
      setLinking(false);
    }
  };

  const unlinkTg = async () => {
    try {
      await alertsAPI.telegramUnlink();
      setTgStatus({ ...tgStatus, linked: false, linked_at: null });
      setDeepLink(null);
      toast({ title: 'Telegram unlinked' });
    } catch (e) {
      console.warn('Telegram unlink failed:', e);
      toast({ title: 'Error', description: 'Unlink failed', variant: 'destructive' });
    }
  };

  const sendTest = async () => {
    try {
      const r = await alertsAPI.telegramTest();
      toast({
        title: r.sent ? 'Test sent' : 'Delivery failed',
        description: r.sent ? 'Check Telegram' : 'Please relink your account',
      });
    } catch (e) {
      console.warn('Test message failed:', e);
      toast({ title: 'Error', description: 'Could not send test', variant: 'destructive' });
    }
  };

  if (!user) {
    return (
      <div className="min-h-screen bg-gray-50 pt-16">
        <div className="max-w-3xl mx-auto px-4 py-12 text-center">
          <h1 className="text-2xl font-bold mb-2">Sign in to manage alerts</h1>
          <Button className="bg-purple-600 hover:bg-purple-700 mt-4" onClick={() => navigate('/auth')}>
            Sign in
          </Button>
        </div>
      </div>
    );
  }

  // Non-Pro users never reach this component — the /alerts route is wrapped
  // in the route-level <Gated> ProGate (see AppRoutes.jsx).

  return (
    <div className="min-h-screen bg-gray-50 pt-16">
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-2">
            <Bell className="text-purple-600" size={26} /> Price Alerts
          </h1>
          <p className="text-gray-500">
            Telegram + mobile push notifications when stocks hit your targets. Checked every 5 min.
          </p>
        </div>

        <TelegramCard
          tgStatus={tgStatus}
          deepLink={deepLink}
          linking={linking}
          polling={polling}
          onStartLink={startLink}
          onUnlink={unlinkTg}
          onSendTest={sendTest}
        />

        <SignalsCard
          signalCats={signalCats}
          subCats={subCats}
          recentByCat={recentByCat}
          runningCat={runningCat}
          tgLinked={!!tgStatus?.linked}
          onToggle={toggleSignalCat}
          onRunNow={runSignalNow}
        />

        <AlertCreateCard form={form} onChange={setForm} onCreate={handleCreate} />

        <AlertHistoryCard events={events} />

        <AlertsListCard
          alerts={alerts}
          loading={loading}
          onToggle={handleToggle}
          onDelete={handleDelete}
        />

        <LegalDisclaimer variant="compact" />
      </div>
    </div>
  );
};

export default Alerts;
